import type { FilterClause, FilterOperator, ListQuery } from './types';

/**
 * `id`, `employee.id`, `employeeId`: i campi il cui valore è una chiave.
 *
 * ⚠️ Solo questi. `{ name: { eq: '' } }` su un campo di testo è **legittimo** — «senza nome» si
 * chiede così — e una regola che segnalasse anche quello griderebbe al lupo su ogni ricerca.
 */
const FIELD_ID = /^(id|[A-Za-z0-9_.]*\.id|[A-Za-z0-9_]*[Ii]d)$/;

/**
 * Le forme in cui un id **assente** arriva fin qui.
 *
 * Le prime tre sono quello che produce un template literal su un valore che non c'è. La quarta è
 * più insidiosa: `Guid.Empty` è sintatticamente un GUID valido, quindi supera qualunque controllo
 * di forma — ma JsonApiDotNetCore lo legge come «non assegnato» e risponde **500** con
 * «*Property 'X.Id' should have been assigned at this point, but it contains its default Guid
 * value*». Verificato sul dev il 06-09-2026.
 */
const ABSENT_ID = new Set(['', 'null', 'undefined', '00000000-0000-0000-0000-000000000000']);

/** `equals(campo,'…')` e `any(campo,'…','…')` dentro a un filtro scritto a mano. */
const RAW_COMPARISON = /\b(equals|any|contains)\(\s*([A-Za-z0-9_.]+)\s*,\s*((?:'[^']*'\s*,?\s*)+)\)/g;

/**
 * UN ID VUOTO IN UN FILTRO — il controllo nel punto da cui passano tutte le richieste.
 *
 * ## Perché esiste
 *
 * Dal **04-09-2026** le chiavi primarie del BE sono `Guid`, e un confronto su un campo-id con un
 * valore assente **non è più una lista vuota**:
 *
 * | valore | risposta del server |
 * |---|---|
 * | `''` | **500** «Property 'X.Id' should have been assigned» |
 * | `'null'` / `'undefined'` | **400**, non è un Guid |
 * | `00000000-…-000000000000` | **500**, stessa frase: `Guid.Empty` conta come «non assegnato» |
 *
 * Prima erano tutti una lista vuota: la schermata diceva «nessun risultato» e nessuno andava a
 * vedere perché. Oggi sono un guasto — e il guasto arriva dal **server**, quindi il messaggio
 * nomina una proprietà di una classe C# invece del punto del nostro codice che ha costruito il
 * filtro. Chi lo legge non ha modo di risalire alla riga.
 *
 * ## Perché a runtime e non nel lint
 *
 * Perché staticamente non si può: decidere se `${clientId}` possa essere vuoto vuole il flusso dei
 * dati, non il testo — e un controllo che segnala ogni filtro costruito per interpolazione sarebbe
 * quasi tutto falsi positivi, cioè il modo documentato di rendere inutile una regola. Qui il
 * filtro è **già costruito**: la domanda diventa banale e i falsi positivi zero.
 *
 * ## Perché lancia invece di togliere la clausola
 *
 * Togliere il filtro cambierebbe la domanda — da «le fatture di questo cliente» a «tutte le
 * fatture» — e mostrare a qualcuno i dati di tutti perché un id era vuoto è peggio del guasto che
 * si sta evitando. Restituire una lista vuota nasconderebbe il difetto, che è com'era prima e per
 * cui nessuno se ne accorgeva.
 *
 * Il posto giusto dove ripararlo è il chiamante — quasi sempre un `enabled:` che manca sulla
 * query — e l'eccezione lo dice, nominando il campo.
 */
export function absentIdsInFilter(query: ListQuery): string[] {
  const faults: string[] = [];

  for (const [campo, clause] of Object.entries(query.filter ?? {})) {
    if (!FIELD_ID.test(campo)) continue;
    for (const [operator, value] of Object.entries(clause ?? {})) {
      /* `isNull`/`isNotNull` non portano un valore, e `{ eq: null }` è la forma **giusta** per
         «senza relazione»: nessuno dei due è un id mancante. */
      if (value === null || value === undefined) continue;
      const values = Array.isArray(value) ? value : [value];
      for (const v of values) {
        if (ABSENT_ID.has(String(v).trim().toLowerCase())) {
          faults.push(`${campo} ${operator} "${String(v)}"`);
        }
      }
    }
  }

  const raw = query.params?.filter;
  if (raw) {
    for (const m of raw.matchAll(RAW_COMPARISON)) {
      if (!FIELD_ID.test(m[2])) continue;
      for (const v of m[3].split(',')) {
        const bare = v.trim().replace(/^'|'$/g, '');
        if (ABSENT_ID.has(bare.toLowerCase())) faults.push(`${m[2]} "${bare}" (filtro grezzo)`);
      }
    }
  }

  return faults;
}

/**
 * Build a JSON:API-compliant query string from a typed ListQuery object.
 *
 * BE conventions (maefwk10 v1.7.0+):
 *   - `type` e route in kebab-case plurale (`/api/erp-employees`, `tasks`).
 *   - Attributi, relationships e nomi di campo in `sort`/`fields[]`/`filter` in camelCase.
 *   - Filter syntax: JADNC function-call expressions `equals(field,'foo')`, NON bracket form.
 *   - **Un solo parametro `filter` per richiesta**: due si compongono in OR lato server (vedi la
 *     nota nel corpo della funzione), quindi qui `query.filter` e `params.filter` si fondono in AND.
 *
 * Il builder accetta camelCase ovunque e li passa as-is sul wire.
 */
export function buildQueryString(query?: ListQuery): string {
  if (!query) return '';
  const params = new URLSearchParams();

  /*
   * Filtro: **un solo** parametro `filter`, sempre — le clausole tipizzate di `query.filter` e
   * l'espressione grezza di `params.filter` finiscono nello stesso, in AND.
   *
   * ⚠️ Perché non due. Un `?filter=A&filter=B` non è un AND e **non** è «il secondo viene
   * ignorato» (che è quello che credevamo, e che sta scritto in qualche commento del repo):
   * JADNC compone i vincoli omonimi dello stesso scope in **OR**. Misurato sul dev il 28-08-2026
   * su `user-tenants`, tutto nella stessa manciata di secondi:
   *
   *     A = equals(tenant.id,'<azienda>')     → 529     B = contains(user.email,'mar') → 34
   *     and(A,B) → 31        A&B (due parametri) → 532        or(A,B) → 532
   *     529 + 34 − 31 = 532: è l'unione, e l'ordine dei due parametri non cambia niente.
   *
   * La conseguenza non è accademica. `fetchList` inietta l'override cross-tenant in
   * `query.filter`; un call site che usa `params.filter` per scrivere un `or(...)` — la ricerca
   * articoli, la ricerca clienti, l'elenco documenti — mandava quindi **due** `filter`, e l'OR con
   * `equals(tenant.id,'<override>')` è vero su ogni riga che il server sta già restituendo: il
   * filtro di ricerca smetteva di filtrare e la lista tornava intera. Con una sola casella di
   * ricerca che «non fa niente» come unico sintomo, e solo lavorando su un'altra azienda.
   *
   * Comporli qui rende l'errore impossibile invece di vietarlo per convenzione. Il BE valuta un
   * `400` sul `filter` ripetuto (`BE-RESPONSE-TO-FE-USER-TENANTS-SCRITTURA-E-FILTRI-v1` §1): quando
   * ci sarà, questa composizione resta comunque la forma giusta della richiesta.
   */
  /* Prima di comporre: un id assente qui dentro diventa un 500 del server con un messaggio che
     non nomina il nostro codice. Vedi `idAssentiNelFiltro`. */
  const guasti = absentIdsInFilter(query);
  if (guasti.length) {
    throw new Error(
      `Filtro con un id assente: ${guasti.join(', ')}.\n`
      + 'Dal 04-09-2026 le chiavi del BE sono `Guid`: un id vuoto non è più una lista vuota, è un '
      + '500. Il punto da riparare è qui, non il filtro — quasi sempre manca un `enabled:` sulla '
      + 'query, che non deve partire finché l\'id non c\'è.',
    );
  }

  const rawFilter = query.params?.filter?.trim();
  if (query.filter || rawFilter) {
    const exprs: string[] = [];
    for (const [field, clause] of Object.entries(query.filter ?? {})) {
      const expr = buildFilterExpression(field, clause);
      if (expr) exprs.push(expr);
    }
    if (rawFilter) exprs.push(rawFilter);
    if (exprs.length === 1) params.append('filter', exprs[0]);
    else if (exprs.length > 1) params.append('filter', `and(${exprs.join(',')})`);
  }

  if (query.sort?.length) {
    params.append('sort', query.sort.join(','));
  }

  if (query.include?.length) {
    params.append('include', query.include.join(','));
  }

  if (query.fields) {
    for (const [type, fields] of Object.entries(query.fields)) {
      if (fields.length) {
        params.append(`fields[${type}]`, fields.join(','));
      }
    }
  }

  if (query.page) {
    if (query.page.number != null) params.append('page[number]', String(query.page.number));
    if (query.page.size != null) {
      /*
       * Il massimo vero e' 1000, non 100: il BE dichiara `MaximumPageSize = new PageSize(1000)`
       * (`JsonApiExtensions.cs`). Misurato sul dev il 01-09-2026: `page[size]=1000` -> 200,
       * `1001` -> 400 «Page size cannot be higher than 1000».
       *
       * Il clamp resta - un 400 per una pagina troppo grande e' un errore che l'utente non sa
       * leggere - ma tagliava a un decimo di quello che il server sa servire: `fetchAllPages`
       * su 3.860 commesse faceva 39 richieste dove ne bastano 4.
       *
       * ⚠️ Una pagina grande senza `fields[]` e' solo un guasto piu' grande: prima il fieldset
       * (§6.2), poi la pagina. Le liste su cui contava sono state sistemate insieme a questo.
       */
      const clamped = Math.min(query.page.size, 1000);
      params.append('page[size]', String(clamped));
    }
  }

  if (query.params) {
    // `filter` no: è già stato composto sopra, nell'unico parametro. Appenderlo qui sarebbe il
    // secondo `filter` — cioè l'OR.
    for (const [k, v] of Object.entries(query.params)) {
      if (k === 'filter') continue;
      params.append(k, v);
    }
  }

  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

function quote(v: string | number | boolean): string {
  if (typeof v === 'string') return `'${v.replace(/'/g, "''")}'`;
  return `'${String(v)}'`;
}

function buildFilterExpression(field: string, clause: FilterClause): string | null {
  const entries = Object.entries(clause).filter(([, v]) => v !== undefined && v !== null);
  if (entries.length === 0) return null;
  const parts: string[] = [];
  for (const [op, val] of entries) {
    parts.push(opExpr(field, op as FilterOperator, val as string | number | boolean | Array<string | number>));
  }
  if (parts.length === 1) return parts[0];
  return `and(${parts.join(',')})`;
}

function opExpr(field: string, op: FilterOperator, val: string | number | boolean | Array<string | number>): string {
  switch (op) {
    case 'eq':
      return `equals(${field},${quote(val as string | number | boolean)})`;
    case 'ne':
      return `not(equals(${field},${quote(val as string | number | boolean)}))`;
    case 'lt':
      return `lessThan(${field},${quote(val as string | number | boolean)})`;
    case 'le':
      return `lessOrEqual(${field},${quote(val as string | number | boolean)})`;
    case 'gt':
      return `greaterThan(${field},${quote(val as string | number | boolean)})`;
    case 'ge':
      return `greaterOrEqual(${field},${quote(val as string | number | boolean)})`;
    case 'like':
      return `contains(${field},${quote(val as string)})`;
    case 'in': {
      const arr = Array.isArray(val) ? val : [val];
      return `any(${field},${arr.map((x) => quote(x as string | number)).join(',')})`;
    }
    case 'notIn': {
      const arr = Array.isArray(val) ? val : [val];
      return `not(any(${field},${arr.map((x) => quote(x as string | number)).join(',')}))`;
    }
    case 'isNull':
      return `equals(${field},null)`;
    /* `null` non si cita: `equals(campo,'null')` cercherebbe la stringa. È il motivo per cui questo
       non si può ottenere con `ne`, che passa sempre da `quote()`. */
    case 'isNotNull':
      return `not(equals(${field},null))`;
    case 'has':
      return `has(${field})`;
    case 'any':
    case 'all':
      // Reserved for relationship-collection filters; not yet supported.
      return `equals(${field},${quote(val as string | number | boolean)})`;
  }
}
